import { memo, useCallback, useMemo } from 'react';
import { cn, Gallery } from '@divlab/divanui';

import * as ConfiguratorStore from '@Stores/Configurator';
import * as Filtrator from '@Stores/Filtrator';
import * as ModalWindows from '@Stores/ModalWindows';
import * as PageProductStore from '@Stores/PageProduct';
import * as ConfiguratorQuery from '@Queries/Configurator';
import { useDeps } from '@Contexts/DI';
import useMedias from '@Hooks/useMedias';
import Collection from '../Collection';
import AllFiltersButton from '../AllFiltersButton';
import OptionCard from '../OptionCard';
import FabricCard from '../FabricCard';
import FabricSampleOrderingPanel from '../FabricSampleOrderingPanel';
import Switcher from '../Switcher';
import ConfiguratorTab from './elems/ConfiguratorTab';
import { useFeatureFlags } from '@Contexts/FeatureFlags';
import VideoConsulationBanner from '../../../VideoConsulationBanner';
import styles from './ConfiguratorTabContainer.module.css';

import type { MouseEvent } from 'react';
import type {
  ConfiguratorData,
  ConstructorCollection,
  ConstructorValueData,
} from '@Pages/PageProduct/typings';
import type { ConfiguratorTabProps } from './elems/ConfiguratorTab';

interface Props extends Omit<ConfiguratorTabProps, 'selected' | 'count'> {
  configurator: ConfiguratorData;
  entityId: Filtrator.FiltratorId;
  needVideoConsultation?: boolean;
}

interface TabFooterProps {
  productId: number;
  needVideoConsultation?: boolean;
}

const TabFooter = (props: TabFooterProps) => {
  const { productId, needVideoConsultation } = props;
  const { videoconsultationAvailable } = useFeatureFlags();
  const { isMobileXS, isMobileM } = useMedias();
  const page = PageProductStore.useData();

  const needConsultation =
    videoconsultationAvailable && needVideoConsultation && isMobileM && !isMobileXS;

  return (
    <Gallery className={styles.slider} cnSlide={styles.cnSlide}>
      {page.readySolutions.canOrderSamples && (
        <FabricSampleOrderingPanel productId={productId} className={styles.slide} />
      )}
      {needConsultation && <VideoConsulationBanner className={styles.slid} />}
    </Gallery>
  );
};

const ConfiguratorTabContainer = (props: Props) => {
  const { tab, configurator, entityId, needVideoConsultation, ...restProps } = props;
  const filtrator = Filtrator.useFiltrator(entityId);
  const appliedFilters = Filtrator.useAppliedValues(entityId);
  const productId = configurator.configuration.id;
  const { applyFilters } = ConfiguratorQuery.useApplyFilters(productId);
  const { changeFilters } = ConfiguratorQuery.useChangeFilters(productId);
  const { resetFilters } = ConfiguratorQuery.useResetFilters(productId);
  const { logger, analytics } = useDeps();
  const isUpholstery = tab.id === 'upholstery';

  const selected = useMemo(() => {
    let selectedParam = configurator.parameterValues.find((pv) => {
      return tab.items[0].parameterIds.includes(pv.parameterIds[0]) && pv.selected;
    });
    if (selectedParam?.type === 'fabric') {
      const currentCollection = configurator.fabricCollections.find(
        (collection) => collection.id === selectedParam.tags.collectionId,
      );
      selectedParam = {
        ...selectedParam,
        title: currentCollection
          ? `${selectedParam.title} ${
              currentCollection.id === 'baseCollection' ? '' : currentCollection.name
            }`
          : selectedParam.title,
      };
    }

    return selectedParam;
  }, [configurator.fabricCollections, configurator.parameterValues, tab.items]);

  const showWarn = useMemo(() => {
    return tab.warning && tab.items.some((item) => item.values.some((val) => val.calcDisabled));
  }, [tab.items, tab.warning]);

  const inStock = useMemo(() => {
    return appliedFilters.some((filter) => filter.parameterId === 'in_stock');
  }, [appliedFilters]);

  // Группируем образцы по коллекциям если не применен ни один фильтр, либо включен переключатель Есть на складе
  const isGroupingByCollections = !appliedFilters?.length || inStock;

  const handleToggleSwitch = useCallback(async () => {
    try {
      await changeFilters();
      applyFilters();
    } catch (error) {
      logger.log(error);
    }
  }, [logger, applyFilters, changeFilters]);

  const handleReset = useCallback(() => {
    resetFilters();
    ModalWindows.close('Filters');
  }, [resetFilters]);

  const handleOpenFilters = useCallback(() => {
    ModalWindows.open('Filters', {
      selectedFilterId: 'all',
      entityId,
      maxWidth: 360,
      onApply: () => applyFilters(),
      onReset: handleReset,
      onChangeFilters: changeFilters,
      isOnProductPage: true,
    });
  }, [entityId, applyFilters, changeFilters, handleReset]);

  const handleClickitem = useCallback(
    (e: MouseEvent, item: ConstructorValueData, tabName: string) => {
      ConfiguratorStore.change(item);
      analytics.dispatchEvent('constructor.productPage.parameterChange', {
        tabName,
        selectedValue: item.title,
      });
    },
    [analytics],
  );

  const handleCollectionClick = useCallback(
    (collection: ConstructorCollection) => {
      ModalWindows.open('CollectionInfo', { collection, types: configurator.types });
    },
    [configurator.types],
  );

  return (
    <ConfiguratorTab
      {...restProps}
      tab={tab}
      count={tab.count}
      slotFooter={
        isUpholstery && (
          <TabFooter productId={productId} needVideoConsultation={needVideoConsultation} />
        )
      }
      selected={selected}
      slotHeader={
        isUpholstery && (
          <div className={cn(styles.container, { [styles.filters]: true })}>
            <AllFiltersButton onClick={handleOpenFilters} />

            {filtrator.filters.map((filter) => {
              if (filter.theme === 'switch') {
                return filter.items.map((item) => {
                  const parameter = filtrator.parameters[item.parameterId];
                  const data = filtrator.parameterValues.find(
                    (val) => val.parameterId === item.parameterId,
                  );

                  return (
                    item.theme === 'switch' && (
                      <Switcher
                        key={item.parameterId}
                        entityId={entityId}
                        item={item}
                        notice={filter.notice}
                        parameter={parameter}
                        data={data}
                        onToggle={handleToggleSwitch}
                      />
                    )
                  );
                });
              }
            })}
          </div>
        )
      }
    >
      {showWarn && <div className={styles.warning}>{tab.warning}</div>}
      {tab.items.map((item) => {
        switch (item.theme) {
          case 'svg':
          case 'square':
          case 'images': {
            return (
              <div className={styles.grid} key={item.parameterIds[0]}>
                {item.values.map((val) => (
                  <OptionCard
                    key={val.value}
                    item={val}
                    onClickItem={(e, constructorValue) =>
                      handleClickitem(e, constructorValue, tab.name)
                    }
                  />
                ))}
              </div>
            );
          }

          case 'fabricFilter': {
            const { fabricCollections } = configurator;
            const filteredCollections = fabricCollections.filter((fc) => !fc.hidden);

            return filteredCollections?.length > 0 && isGroupingByCollections ? (
              <div className={styles.container} key={item.parameterIds[0]}>
                {filteredCollections.map((collection) => {
                  if (collection.fabrics?.length < 1 || collection.fabrics?.every((f) => f.hidden))
                    return null;
                  return (
                    <Collection
                      key={collection.id}
                      collection={collection}
                      className={styles.collection}
                      tabName={tab.name}
                      onClickItem={(e, constructorValue) =>
                        handleClickitem(e, constructorValue, tab.name)
                      }
                      onClickCollection={handleCollectionClick}
                    />
                  );
                })}
              </div>
            ) : (
              <div className={styles.grid} key={item.parameterIds[0]}>
                {item.values.map((fabric, index) => {
                  const collection = fabricCollections.find(
                    (col) => col.id === fabric.tags.collectionId,
                  );

                  return (
                    <FabricCard
                      data-testid={`color${index + 1}`}
                      key={`${fabric.value}_${collection.id}`}
                      fabric={fabric}
                      collection={collection}
                      tabName={tab.name}
                      onClickItem={(e, constructorValue) =>
                        handleClickitem(e, constructorValue, tab.name)
                      }
                      onClickCollection={() => handleCollectionClick(collection)}
                    />
                  );
                })}
              </div>
            );
          }

          default: {
            return null;
          }
        }
      })}
    </ConfiguratorTab>
  );
};

export default memo(ConfiguratorTabContainer);
